import React, { useContext, useEffect, useState } from "react";
import classNames from "classnames";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";

import {
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
} from "reactstrap";

import OrderService from "../service/order.service";
import { ApiContext } from "../contexts/ApiContext";
import WhatsappStatusBar from "../components/Whatsapp/WhatsappStatusBar";
import InventoryService from "../service/inventory.service";
import { accountPath } from "../utils/accountPath";

const STATUS_LABELS = {
  PENDING: "Pendente",
  CONFIRMED: "Confirmado",
  PREPARING: "Preparando",
  READY_FOR_DELIVERY: "Pronto p/ entrega",
  OUT_FOR_DELIVERY: "Saiu p/ entrega",
  DELIVERED: "Entregue",
  CANCELLED: "Cancelado",
};

const PERIODS = [
  { key: "7d", label: "7 dias", days: 7 },
  { key: "30d", label: "30 dias", days: 30 },
  { key: "month", label: "Mês atual", days: null },
];

const brl = (cents) =>
  ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const rangeFor = (key) => {
  const to = new Date();
  let from = new Date();
  if (key === "month") {
    from = new Date(to.getFullYear(), to.getMonth(), 1);
  } else {
    const period = PERIODS.find((p) => p.key === key);
    from = new Date(Date.now() - (period?.days || 30) * 24 * 60 * 60 * 1000);
  }
  return { from: from.toISOString(), to: to.toISOString() };
};

const lineOptions = {
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: {
      ticks: {
        color: "#9a9a9a",
        callback: (v) => "R$ " + v,
      },
      grid: { color: "rgba(29,140,248,0.1)", drawBorder: false },
    },
    x: {
      ticks: { color: "#9a9a9a" },
      grid: { color: "rgba(29,140,248,0.05)", drawBorder: false },
    },
  },
};

const barOptions = {
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: {
      ticks: { color: "#9a9a9a", precision: 0, stepSize: 1 },
      grid: { color: "rgba(29,140,248,0.1)", drawBorder: false },
    },
    x: {
      ticks: { color: "#9a9a9a" },
      grid: { color: "rgba(29,140,248,0.05)", drawBorder: false },
    },
  },
};

const doughnutOptions = {
  maintainAspectRatio: false,
  plugins: { legend: { position: "bottom", labels: { color: "#9a9a9a" } } },
};

const DOUGHNUT_COLORS = [
  "#fbc658",
  "#51cbce",
  "#6bd098",
  "#ef8157",
  "#1f8ef1",
  "#7c3aed",
  "#9a9a9a",
];

function Dashboard() {
  const { account } = useContext(ApiContext);
  const navigate = useNavigate();
  const accountId = account?.id;
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inventoryOverview, setInventoryOverview] = useState(null);

  useEffect(() => {
    if (!accountId) return;
    let active = true;
    setLoading(true);
    const { from, to } = rangeFor(period);
    OrderService.getAnalytics({ accountId, from, to })
      .then((res) => {
        if (active) setData(res);
      })
      .catch((err) => console.error("Falha ao carregar analytics", err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [accountId, period]);

  useEffect(() => {
    if (!accountId || !account?.inventoryEnabled) {
      setInventoryOverview(null);
      return;
    }
    let active = true;
    InventoryService.getOverview(accountId)
      .then((result) => active && setInventoryOverview(result))
      .catch((error) => console.error("Falha ao carregar resumo do estoque", error));
    return () => { active = false; };
  }, [accountId, account?.inventoryEnabled]);

  const revenueChart = (canvas) => {
    const ctx = canvas.getContext("2d");
    const gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
    gradientStroke.addColorStop(1, "rgba(29,140,248,0.25)");
    gradientStroke.addColorStop(0.4, "rgba(29,140,248,0.05)");
    gradientStroke.addColorStop(0, "rgba(29,140,248,0)");
    return {
      labels: (data?.revenueByDay || []).map((d) => {
        const [, m, day] = d.day.split("-");
        return `${day}/${m}`;
      }),
      datasets: [
        {
          label: "Faturamento",
          fill: true,
          backgroundColor: gradientStroke,
          borderColor: "#1f8ef1",
          borderWidth: 2,
          pointBackgroundColor: "#1f8ef1",
          pointBorderColor: "rgba(255,255,255,0)",
          pointHoverBackgroundColor: "#1f8ef1",
          pointRadius: 4,
          pointHoverRadius: 4,
          tension: 0.4,
          data: (data?.revenueByDay || []).map((d) => (d.totalCents || 0) / 100),
        },
      ],
    };
  };

  const ordersChart = (canvas) => {
    const ctx = canvas.getContext("2d");
    const gradientStroke = ctx.createLinearGradient(0, 230, 0, 50);
    gradientStroke.addColorStop(1, "rgba(124,58,237,0.4)");
    gradientStroke.addColorStop(0.4, "rgba(124,58,237,0.1)");
    gradientStroke.addColorStop(0, "rgba(124,58,237,0)");
    return {
      labels: (data?.ordersByDay || []).map((d) => {
        const [, m, day] = d.day.split("-");
        return `${day}/${m}`;
      }),
      datasets: [
        {
          label: "Pedidos",
          fill: true,
          backgroundColor: gradientStroke,
          hoverBackgroundColor: gradientStroke,
          borderColor: "#7c3aed",
          borderWidth: 2,
          borderRadius: 6,
          maxBarThickness: 40,
          data: (data?.ordersByDay || []).map((d) => d.count),
        },
      ],
    };
  };

  const statusChart = {
    labels: (data?.ordersByStatus || []).map((s) => STATUS_LABELS[s.status] || s.status),
    datasets: [
      {
        data: (data?.ordersByStatus || []).map((s) => s.count),
        backgroundColor: DOUGHNUT_COLORS,
        borderWidth: 0,
      },
    ],
  };

  return (
    <div className="content">
      <Row>
        <Col xs="12">
          <WhatsappStatusBar />
        </Col>
      </Row>
      <Row>
        <Col xs="12">
          <Card className="card-chart">
            <CardHeader>
              <Row>
                <Col className="text-left" sm="6">
                  <h5 className="card-category">Faturamento no período</h5>
                  <CardTitle tag="h2">
                    {loading ? "Carregando..." : brl(data?.revenueCents)}
                  </CardTitle>
                </Col>
                <Col sm="6">
                  <ButtonGroup className="btn-group-toggle float-right" data-toggle="buttons">
                    {PERIODS.map((p) => (
                      <Button
                        key={p.key}
                        tag="label"
                        color="info"
                        size="sm"
                        className={classNames("btn-simple", { active: period === p.key })}
                        onClick={() => setPeriod(p.key)}
                      >
                        <span>{p.label}</span>
                      </Button>
                    ))}
                  </ButtonGroup>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <div className="chart-area">
                <Line data={revenueChart} options={lineOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-success">
                    <i className="tim-icons icon-money-coins" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Ticket médio</p>
                    <CardTitle tag="h3">{brl(data?.avgTicketCents)}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-primary">
                    <i className="tim-icons icon-paper" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Pedidos</p>
                    <CardTitle tag="h3">{data?.ordersCount ?? 0}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-warning">
                    <i className="tim-icons icon-bell-55" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">Em andamento agora</p>
                    <CardTitle tag="h3">{data?.pendingNow ?? 0}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        <Col lg="3" md="6">
          <Card className="card-stats">
            <CardBody>
              <Row>
                <Col xs="5">
                  <div className="info-icon text-center icon-info">
                    <i className="tim-icons icon-delivery-fast" />
                  </div>
                </Col>
                <Col xs="7">
                  <div className="numbers">
                    <p className="card-category">
                      Hoje · {data?.todayOrders ?? 0} pedidos
                    </p>
                    <CardTitle tag="h3">{brl(data?.todayRevenueCents)}</CardTitle>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
        {account?.inventoryEnabled && (
          <Col lg="3" md="6">
            <Card
              className="card-stats"
              role="button"
              tabIndex="0"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(accountPath(accountId, "/estoque?status=LOW"))}
              onKeyDown={(event) => event.key === "Enter" && navigate(accountPath(accountId, "/estoque?status=LOW"))}
            >
              <CardBody>
                <Row>
                  <Col xs="5">
                    <div className="info-icon text-center icon-danger">
                      <i className="tim-icons icon-basket-simple" />
                    </div>
                  </Col>
                  <Col xs="7">
                    <div className="numbers">
                      <p className="card-category">Estoque baixo</p>
                      <CardTitle tag="h3">{inventoryOverview?.lowItems ?? "—"}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>

      <Row>
        <Col xs="12">
          <Card className="card-chart">
            <CardHeader>
              <h5 className="card-category">Volume no período</h5>
              <CardTitle tag="h4">Pedidos por dia</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="chart-area">
                <Bar data={ordersChart} options={barOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg="5" md="12">
          <Card className="card-chart">
            <CardHeader>
              <h5 className="card-category">Distribuição</h5>
              <CardTitle tag="h4">Pedidos por status</CardTitle>
            </CardHeader>
            <CardBody>
              <div className="chart-area">
                {(data?.ordersByStatus || []).length ? (
                  <Doughnut data={statusChart} options={doughnutOptions} />
                ) : (
                  <p className="text-muted text-center">Sem dados no período</p>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg="7" md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Itens mais vendidos</CardTitle>
            </CardHeader>
            <CardBody>
              <Table className="tablesorter" responsive>
                <thead className="text-primary">
                  <tr>
                    <th>Item</th>
                    <th className="text-center">Qtd</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.topItems || []).length ? (
                    data.topItems.map((it, i) => (
                      <tr key={i}>
                        <td>{it.name}</td>
                        <td className="text-center">{it.quantity}</td>
                        <td className="text-right">{brl(it.totalCents)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-muted text-center">
                        Sem dados no período
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default Dashboard;
